/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * "Thanh toán" (`/sales?tab=payments`, detail `/payments/:id`): payment requests by bank-transfer
 * QR (owner decision 2026-10-02: the only way customers pay) — wallet top-ups and orders waiting
 * for their transfer — with whether the money arrived, and the refunds going back out. A request's
 * page shows its code and payment link while it is open, the bank transactions that named it, the
 * customer's attempts at a gateway's page (ADR-028 stage 4) and its approvals; staff confirm a
 * transfer seen on the statement (a second person approves at or above the core setting's
 * threshold), ask a gateway where an attempt stands, refund a gateway payment to the card or
 * account that paid (ADR-028 decision 10: out of the wallet, a second person at or above the
 * threshold), or cancel the request (subsystems/commerce/facade/adapters/admin-api/src/admin/
 * bank_payments.rs, payment_refunds.rs).
 */

import { CircleCheck, CircleX, ClipboardCheck, RefreshCw, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSearchParams } from '@/lib/scopedParams';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { api, isApiError } from '../../api';
import type {
  ConfirmPaymentBody,
  Payment,
  PaymentRequest,
  PaymentRequestApproval,
  PaymentRequestDetail,
  PaymentSession,
  PaymentStatus,
  Refund,
  RefundStatus,
  ResolveSourceRefundBody,
  SourceRefund,
  SourceRefundBody,
} from '../../api/areas/commerce';
import { Callout, DataTable, Field, FilterBar, Section, StatCard, StatGrid, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { DomainLink } from '../../components/Ids';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { SystemReason } from '../../components/SystemReason';
import { useWrite, WriteError } from '../../components/Write';
import { useAccess } from '../../lib/access';
import { formatDateTime, formatMoney } from '../../lib/format';
import { mayHandlePayments, mayMakeCodes, moneyMisfit, needsSecondApproval, wholeDong } from '../../lib/qrPayment';
import { useApi } from '../../lib/useApi';
import { ActionDialog, FormFooter, ReasonField, type DialogBodyProps } from '../operator/shared';
import { BankTxTable, bankTxPath } from './BankTransactionsPage';
import { DuplicateReference, duplicateMatches } from './DuplicateReference';
import { QrCard } from './PaymentQr';
import { CustomerListFilter } from './CustomerListFilter';
import {
  Amount,
  ATTEMPT_STATUS,
  clearParams,
  CustomerRef,
  DOC_TYPE,
  FilterSelect,
  InvoiceRef,
  JournalRef,
  Label,
  mutedSmall,
  ORDER_ITEM_STATUS,
  ORDER_STATUS,
  OrderRef,
  Pager,
  PAYMENT_STATUS,
  paymentPath,
  PaymentRef,
  PostingsTable,
  RefreshButton,
  REFUND_STATUS,
  RefundRef,
  ResetFilters,
  RichText,
  setParam,
  Status,
  useEnumLabel,
  useKeyset,
  When,
} from './shared';

const STATUSES: PaymentStatus[] = ['PENDING', 'SUCCEEDED', 'CANCELLED'];
const FILTER_KEYS = ['status', 'customer_id', 'attention'] as const;

function RefundsTable({ rows, empty, showCustomer = true }: { rows: Refund[]; empty?: string; showCustomer?: boolean }) {
  const { t } = useTranslation('commerce');
  const columns: Column<Refund>[] = [
    { key: 'refund', header: t('payments.refundCol.refund'), cell: (r) => <RefundRef id={r.id} domain={r.domain_name} /> },
    ...(showCustomer ? [{ key: 'customer', header: t('payments.refundCol.customer'), cell: (r: Refund) => <CustomerRef id={r.customer_id} name={r.customer_name} /> }] : []),
    { key: 'order', header: t('payments.refundCol.order'), cell: (r) => <OrderRef id={r.order_id} domain={r.order_domain} count={r.order_item_count} at={r.order_placed_at} /> },
    { key: 'reason', header: t('payments.refundCol.reason'), className: 'whitespace-normal', cell: (r) => <span className="block min-w-40"><SystemReason reason={r.reason} /></span> },
    { key: 'status', header: t('payments.refundCol.status'), cell: (r) => <Status group="refundStatus" tones={REFUND_STATUS} value={r.status} /> },
    { key: 'amount', header: t('payments.refundCol.amount'), numeric: true, cell: (r) => <Amount amount={r.amount} currency={r.currency} /> },
    { key: 'entry', header: t('payments.refundCol.entry'), cell: (r) => <JournalRef id={r.journal_entry_id} description={r.entry_description} /> },
    { key: 'created', header: t('payments.refundCol.created'), cell: (r) => <When at={r.created_at} /> },
  ];
  return <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} empty={empty ? <EmptyState title={empty} /> : undefined} />;
}

function Refunds() {
  const { t } = useTranslation('commerce');
  const [params, setParams] = useSearchParams();
  const status = (params.get('refund_status') ?? '') as RefundStatus | '';
  const pager = useKeyset(status);
  const result = useApi((signal) => api.listRefunds({ status: status || undefined, limit: 50, before: pager.before }, { signal }), [status, pager.before]);
  return (
    <section id="refunds" className="scroll-mt-20">
      <Section title={t('payments.refunds')} flush>
        <div className="px-5">
          <FilterBar>
            <FilterSelect label={t('common.status')} value={status} options={Object.keys(REFUND_STATUS)} group="refundStatus" onChange={(v) => setParams(setParam(params, 'refund_status', v))} />
          </FilterBar>
        </div>
        {result.status === 'loading' ? (
          <div className="px-5">
            <Loading rows={3} />
          </div>
        ) : null}
        {result.status === 'error' ? (
          <div className="px-5 pb-5">
            <DataError error={result.error} onRetry={result.reload} title={t('payments.refundsLoadError')} />
          </div>
        ) : null}
        {result.status === 'success' ? (
          <>
            <RefundsTable rows={result.data.items} empty={t('payments.refundsEmpty')} />
            <Pager cursors={pager.cursors} next={result.data.next_before} onNewer={pager.newer} onOlder={pager.older} />
          </>
        ) : null}
      </Section>
    </section>
  );
}

/** `/sales?tab=payments`: payment requests by bank-transfer QR, and the refunds going back out. */
export function PaymentsPage() {
  const { t } = useTranslation('commerce');
  useDocumentTitle(t('payments.title'));
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as PaymentStatus | '';
  const customer = params.get('customer_id') ?? '';
  const attention = params.get('attention') === 'sessions';
  const pager = useKeyset(`${status}|${customer}|${attention}`);
  const result = useApi(
    (signal) =>
      api.listPaymentRequests(
        { status: status || undefined, customer_id: customer || undefined, attention: attention ? 'sessions' : undefined, limit: 50, before: pager.before },
        { signal },
      ),
    [status, customer, attention, pager.before],
  );
  const columns: Column<PaymentRequest>[] = [
    {
      key: 'code',
      header: t('payments.col.code'),
      cell: (p) => (
        <Link to={paymentPath(p.id)} className="font-mono text-sm font-medium text-primary hover:underline">
          {p.code ?? p.id.slice(0, 8)}
        </Link>
      ),
    },
    { key: 'customer', header: t('payments.col.customer'), cell: (p) => <CustomerRef id={p.customer_id} name={p.customer_name} /> },
    {
      key: 'purpose',
      header: t('payments.col.purpose'),
      cell: (p) => (
        <span className="inline-flex flex-col gap-0.5">
          <Label group="paymentPurpose" value={p.purpose} />
          {p.order_id ? <OrderRef id={p.order_id} domain={p.order_domains?.split(', ')[0] ?? null} /> : null}
        </span>
      ),
    },
    { key: 'status', header: t('payments.col.status'), cell: (p) => <Status group="paymentStatus" tones={PAYMENT_STATUS} value={p.status} /> },
    { key: 'amount', header: t('payments.col.amount'), numeric: true, cell: (p) => <Amount amount={p.amount} currency={p.currency} className="font-semibold" /> },
    { key: 'settled', header: t('payments.col.settled'), numeric: true, cell: (p) => <Amount amount={p.settled_amount} currency={p.currency} /> },
    { key: 'expires', header: t('payments.col.expires'), cell: (p) => (p.status === 'PENDING' ? <When at={p.expires_at} /> : <span className="text-muted-foreground">—</span>) },
    { key: 'created', header: t('payments.col.created'), cell: (p) => <When at={p.created_at} /> },
  ];
  return (
    <>
      <PageHeader title={t('payments.title')} crumbs={[{ label: t('crumb') }]} subtitle={<RichText k="payments.subtitle" />} actions={<RefreshButton onClick={result.reload} />} />
      <div className="flex flex-col gap-6">
        <div>
          <FilterBar actions={<ResetFilters show={Boolean(status || customer || attention)} onReset={() => setParams(clearParams(params, FILTER_KEYS))} />}>
            <CustomerListFilter value={customer} name={result.status === 'success' ? result.data.items[0]?.customer_name : undefined} onChange={(v) => setParams(setParam(params, 'customer_id', v))} />
            <FilterSelect label={t('common.status')} value={status} options={STATUSES} group="paymentStatus" className="sm:w-52" onChange={(v) => setParams(setParam(params, 'status', v))} />
            <Button
              type="button"
              variant={attention ? 'secondary' : 'outline'}
              aria-pressed={attention}
              title={t('payments.attentionHint')}
              onClick={() => setParams(setParam(params, 'attention', attention ? '' : 'sessions'))}
            >
              {t('payments.attention')}
            </Button>
          </FilterBar>
          <Section flush>
            {result.status === 'loading' ? (
              <div className="px-5">
                <Loading rows={5} />
              </div>
            ) : null}
            {result.status === 'error' ? (
              <div className="px-5 pb-5">
                <DataError error={result.error} onRetry={result.reload} title={t('payments.loadError')} />
              </div>
            ) : null}
            {result.status === 'success' ? (
              <>
                <DataTable columns={columns} rows={result.data.items} rowKey={(p) => p.id} empty={<EmptyState title={t('payments.empty')} />} />
                <Pager cursors={pager.cursors} next={result.data.next_before} onNewer={pager.newer} onOlder={pager.older} />
              </>
            ) : null}
          </Section>
        </div>
        <Refunds />
      </div>
    </>
  );
}

function ConfirmForm({ detail, close, changed }: DialogBodyProps & { detail: PaymentRequestDetail }) {
  const { t } = useTranslation('commerce');
  const p = detail.request;
  const [amount, setAmount] = useState(p.amount);
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const w = useWrite((b: ConfirmPaymentBody, opts) => api.confirmPaymentRequest(p.id, b, opts));
  const duplicates = w.state.phase === 'error' ? duplicateMatches(w.state.error) : null;
  const value = wholeDong(amount);
  const wrong = amount.trim() !== '' && !value;
  const threshold = formatMoney({ amount: detail.confirm_threshold, currency: p.currency });
  if (w.state.phase === 'done') {
    const r = w.state.result;
    return (
      <div className="grid gap-4">
        <Callout tone={r.status === 'SUCCEEDED' ? 'ok' : 'info'}>{r.status === 'SUCCEEDED' ? t('payment.confirm.done') : t('payment.confirm.requested')}</Callout>
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }
  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (value && reference.trim() && reason.trim())
          void w.run({ amount: value, bank_reference: reference.trim(), reason: reason.trim(), acknowledge_duplicate: acknowledged }).then((r) => r && changed());
      }}
    >
      <Field label={t('payment.confirm.amount')} hint={wrong ? <span className="text-critical">{t('payment.confirm.amountInvalid')}</span> : t('payment.confirm.amountHint')}>
        <Input inputMode="numeric" autoComplete="off" value={amount} aria-invalid={wrong} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      <Field label={t('payment.confirm.reference')} hint={t('payment.confirm.referenceHint')}>
        <Input
          autoComplete="off"
          maxLength={100}
          required
          value={reference}
          onChange={(e) => {
            setReference(e.target.value);
            setAcknowledged(false);
            if (w.state.phase === 'error') w.reset();
          }}
        />
      </Field>
      <ReasonField value={reason} onChange={setReason} />
      {value && needsSecondApproval(value, detail.confirm_threshold) ? <Callout tone="info">{t('payment.confirm.threshold', { threshold })}</Callout> : null}
      {duplicates ? (
        <DuplicateReference matches={duplicates} currency={p.currency} checked={acknowledged} onChange={setAcknowledged} />
      ) : w.state.phase === 'error' ? (
        <WriteError error={w.state.error} />
      ) : null}
      <FormFooter busy={w.busy} disabled={!value || !reference.trim() || !reason.trim() || (duplicates !== null && !acknowledged)} submit={t('payment.confirm.submit')} onClose={close} />
    </form>
  );
}

function CancelForm({ id, close, changed }: DialogBodyProps & { id: string }) {
  const { t } = useTranslation('commerce');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { reason: string }, opts) => api.cancelPaymentRequest(id, b, opts));
  if (w.state.phase === 'done') {
    return (
      <div className="grid gap-4">
        <Callout tone="neutral">{w.state.result.order_cancelled ? t('payment.cancel.orderCancelled') : t('payment.cancel.done')}</Callout>
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }
  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (reason.trim()) void w.run({ reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      <ReasonField value={reason} onChange={setReason} />
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={t('payment.cancel.submit')} onClose={close} danger />
    </form>
  );
}

function Approvals({ rows }: { rows: PaymentRequestApproval[] }) {
  const { t } = useTranslation('commerce');
  const columns: Column<PaymentRequestApproval>[] = [
    { key: 'status', header: t('payment.approvalCol.status'), cell: (a) => <Label group="approvalStatus" value={a.status} /> },
    { key: 'amount', header: t('payment.approvalCol.amount'), numeric: true, cell: (a) => <Amount amount={a.amount} currency="VND" /> },
    { key: 'by', header: t('payment.approvalCol.requestedBy'), cell: (a) => a.requested_by_name ?? '—' },
    { key: 'decided', header: t('payment.approvalCol.decidedBy'), cell: (a) => a.decided_by_name ?? '—' },
    { key: 'at', header: t('payment.approvalCol.requestedAt'), className: 'whitespace-nowrap', cell: (a) => formatDateTime(a.requested_at) },
  ];
  return <DataTable columns={columns} rows={rows} rowKey={(a) => a.id} empty={<EmptyState title={t('payment.noApprovals')} />} />;
}

/** "Tra trạng thái": asks the gateway where the attempt stands; the server records its answer. */
function SyncSession({ s, onDone }: { s: PaymentSession; onDone: () => void }) {
  const { t } = useTranslation('commerce');
  const w = useWrite((_: null, opts) => api.syncPaymentSession(s.id, opts));
  return (
    <span className="grid justify-items-end gap-1">
      <Button type="button" size="xs" variant="outline" disabled={w.busy} onClick={() => void w.run(null).then((r) => r && onDone())}>
        <RefreshCw className={w.busy ? 'animate-spin' : undefined} /> {t('payment.session.sync')}
      </Button>
      {s.checked_at ? <span className={mutedSmall}>{t('payment.session.checkedAt', { at: formatDateTime(s.checked_at) })}</span> : null}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
    </span>
  );
}

/** What can still go back to the payer's source from a session: the money it brought less what was refunded and not given back (whole đồng). */
function refundable(s: PaymentSession, refunds: SourceRefund[]): bigint {
  const dong = (a: string) => BigInt(a.split('.')[0] || '0');
  if (!s.money_amount) return 0n;
  return refunds.filter((r) => r.session_id === s.id && r.status !== 'FAILED').reduce((left, r) => left - dong(r.amount), dong(s.money_amount));
}

/** "Hoàn tiền về nguồn": the wallet pays the amount back to the card or account that paid (a second person at or above the threshold). */
function RefundForm({ session, max, threshold, close, changed }: DialogBodyProps & { session: PaymentSession; max: bigint; threshold: string }) {
  const { t } = useTranslation('commerce');
  const [amount, setAmount] = useState(max.toString());
  const [reason, setReason] = useState('');
  const w = useWrite((b: SourceRefundBody, opts) => api.refundPaymentSession(session.id, b, opts));
  const value = wholeDong(amount);
  const wrong = amount.trim() !== '' && (!value || BigInt(value) > max);
  const money = (a: string) => formatMoney({ amount: a, currency: 'VND' });
  if (w.state.phase === 'done') {
    const done = 'refund' in w.state.result;
    return (
      <div className="grid gap-4">
        <Callout tone={done ? 'ok' : 'info'}>{done ? t('payment.refund.done') : t('payment.refund.requested')}</Callout>
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }
  const error = w.state.phase === 'error' ? w.state.error : null;
  const available = isApiError(error) && error.code === 'INSUFFICIENT_FUNDS' ? (error.details as { available?: string } | null | undefined)?.available : undefined;
  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (value && !wrong && reason.trim()) void w.run({ amount: value, reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      <Field label={t('payment.refund.amount')} hint={wrong ? <span className="text-critical">{t('payment.refund.amountInvalid')}</span> : t('payment.refund.amountHint', { max: money(max.toString()) })}>
        <Input inputMode="numeric" autoComplete="off" value={amount} aria-invalid={wrong} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      <ReasonField value={reason} onChange={setReason} />
      {value && needsSecondApproval(value, threshold) ? <Callout tone="info">{t('payment.refund.threshold', { threshold: money(threshold) })}</Callout> : null}
      {available !== undefined ? <Callout tone="warn">{t('payment.refund.insufficient', { available: money(available) })}</Callout> : error ? <WriteError error={error} /> : null}
      <FormFooter busy={w.busy} disabled={!value || wrong || !reason.trim()} submit={t('payment.refund.submit')} onClose={close} danger />
    </form>
  );
}

/** "Ghi nhận kết quả": a refund the gateway never reported, settled by what its merchant admin shows (failed: back to the wallet, a second person at or above the threshold). */
function ResolveForm({ refund, threshold, close, changed }: DialogBodyProps & { refund: SourceRefund; threshold: string }) {
  const { t } = useTranslation('commerce');
  const [status, setStatus] = useState<ResolveSourceRefundBody['status']>('COMPLETED');
  const [reason, setReason] = useState('');
  const w = useWrite((b: ResolveSourceRefundBody, opts) => api.resolveSourceRefund(refund.id, b, opts));
  if (w.state.phase === 'done') {
    const done = 'refund' in w.state.result;
    return (
      <div className="grid gap-4">
        <Callout tone={done ? 'ok' : 'info'}>{done ? t('payment.resolve.done') : t('payment.resolve.requested')}</Callout>
        <FormFooter busy={false} onClose={close} />
      </div>
    );
  }
  const money = (a: string) => formatMoney({ amount: a, currency: 'VND' });
  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (reason.trim()) void w.run({ status, reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      <Field label={t('payment.resolve.status')}>
        <Select value={status} onValueChange={(v) => setStatus(v as ResolveSourceRefundBody['status'])}>
          <SelectTrigger className="w-full bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="COMPLETED">{t('payment.resolve.completed')}</SelectItem>
            <SelectItem value="FAILED">{t('payment.resolve.failed')}</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <ReasonField value={reason} onChange={setReason} />
      {status === 'FAILED' && needsSecondApproval(refund.amount, threshold) ? <Callout tone="info">{t('payment.resolve.threshold', { threshold: money(threshold) })}</Callout> : null}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!reason.trim()} submit={t('payment.resolve.submit')} onClose={close} danger={status === 'FAILED'} />
    </form>
  );
}

/** The refunds to the payer's source of the request's gateway sessions; staff settle one the gateway never reported. */
function SourceRefunds({ rows, threshold, reload }: { rows: SourceRefund[]; threshold: string; reload: () => void }) {
  const { t } = useTranslation('commerce');
  const access = useAccess();
  const columns: Column<SourceRefund>[] = [
    { key: 'status', header: t('payment.refundCol.status'), cell: (r) => <Status group="sourceRefundStatus" tones={REFUND_STATUS} value={r.status} /> },
    { key: 'amount', header: t('payment.refundCol.amount'), numeric: true, cell: (r) => <Amount amount={r.amount} currency={r.currency} /> },
    { key: 'reason', header: t('payment.refundCol.reason'), className: 'whitespace-normal', cell: (r) => <span className="block min-w-40">{r.reason ?? '—'}</span> },
    { key: 'ref', header: t('payment.refundCol.providerRef'), cell: (r) => (r.provider_ref ? <CopyText value={r.provider_ref} /> : '—') },
    { key: 'code', header: t('payment.refundCol.providerCode'), cell: (r) => (r.provider_code ? <code className="text-xs">{r.provider_code}</code> : '—') },
    { key: 'entry', header: t('payment.refundCol.entry'), cell: (r) => <JournalRef id={r.journal_entry_id} /> },
    { key: 'created', header: t('payment.refundCol.created'), cell: (r) => <When at={r.created_at} /> },
    { key: 'checked', header: t('payment.refundCol.checked'), cell: (r) => (r.checked_at ? <When at={r.checked_at} /> : '—') },
    ...(mayHandlePayments(access.grants, access.client)
      ? [
          {
            key: 'resolve',
            header: '',
            cell: (r: SourceRefund) =>
              r.status === 'PENDING_PROVIDER' ? (
                <ActionDialog label={t('payment.resolve.action')} icon={ClipboardCheck} variant="ghost" title={t('payment.resolve.title')} description={t('payment.resolve.description')} onChanged={reload}>
                  {(d) => <ResolveForm refund={r} threshold={threshold} {...d} />}
                </ActionDialog>
              ) : null,
          },
        ]
      : []),
  ];
  return <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} empty={<EmptyState title={t('payment.noSourceRefunds')} />} />;
}

/** The customer's attempts at a gateway's page, with the money each one brought. */
function Sessions({ rows, refunds, threshold, reload }: { rows: PaymentSession[]; refunds: SourceRefund[]; threshold: string; reload: () => void }) {
  const { t } = useTranslation('commerce');
  const label = useEnumLabel('commerce');
  const access = useAccess();
  const columns: Column<PaymentSession>[] = [
    { key: 'status', header: t('payment.sessionCol.status'), cell: (s) => <Status group="attemptStatus" tones={ATTEMPT_STATUS} value={s.status} /> },
    {
      key: 'gateway',
      header: t('payment.sessionCol.gateway'),
      cell: (s) => (
        <span className="inline-flex flex-col gap-0.5">
          <span>{`${label('bankFeed', s.module)} · ${s.instance_key}`}</span>
          <span className={mutedSmall}>{s.instrument ?? t('payment.session.onGatewayPage')}</span>
        </span>
      ),
    },
    { key: 'ref', header: t('payment.sessionCol.providerRef'), cell: (s) => (s.provider_ref ? <CopyText value={s.provider_ref} /> : '—') },
    { key: 'code', header: t('payment.sessionCol.providerCode'), cell: (s) => (s.provider_code ? <code className="text-xs">{s.provider_code}</code> : '—') },
    { key: 'created', header: t('payment.sessionCol.created'), cell: (s) => <When at={s.created_at} /> },
    { key: 'expires', header: t('payment.sessionCol.expires'), cell: (s) => <When at={s.expires_at} /> },
    {
      key: 'money',
      header: t('payment.sessionCol.money'),
      className: 'whitespace-normal',
      cell: (s) => {
        if (!s.bank_transaction_id) return '—';
        const misfit = moneyMisfit(s);
        return (
          <span className="inline-flex min-w-40 flex-col gap-0.5">
            <Link to={bankTxPath(s.bank_transaction_id)} className="text-primary hover:underline">
              <Amount amount={s.money_amount} currency="VND" />
            </Link>
            {misfit ? (
              <span className="text-xs text-warning">
                {misfit === 'AMOUNT' ? t('payment.session.amountDiffers', { amount: formatMoney({ amount: s.money_amount ?? '', currency: 'VND' }) }) : t('payment.session.notCaptured')}
              </span>
            ) : null}
          </span>
        );
      },
    },
    ...(mayHandlePayments(access.grants, access.client)
      ? [
          {
            key: 'sync',
            header: '',
            cell: (s: PaymentSession) => {
              const max = refundable(s, refunds);
              const pending = refunds.some((r) => r.session_id === s.id && r.status === 'PENDING_PROVIDER');
              return (
                <span className="grid justify-items-end gap-1">
                  <SyncSession s={s} onDone={reload} />
                  {max > 0n && !pending ? (
                    <ActionDialog label={t('payment.refund.action')} icon={Undo2} variant="ghost" title={t('payment.refund.title')} description={t('payment.refund.description')} onChanged={reload}>
                      {(d) => <RefundForm session={s} max={max} threshold={threshold} {...d} />}
                    </ActionDialog>
                  ) : null}
                </span>
              );
            },
          },
        ]
      : []),
  ];
  return <DataTable columns={columns} rows={rows} rowKey={(s) => s.id} empty={<EmptyState title={t('payment.noSessions')} />} />;
}

/** A payment request: its code while open, what arrived, the approvals of a manual confirmation. */
function RequestDetail({ detail, reload }: { detail: PaymentRequestDetail; reload: () => void }) {
  const { t } = useTranslation('commerce');
  const label = useEnumLabel('commerce');
  const access = useAccess();
  const p = detail.request;
  const open = p.status === 'PENDING';
  const crumbs = [{ label: t('crumb') }, { label: t('payments.title'), to: '/sales?tab=payments' }];
  const settledTx = detail.transactions.find((x) => x.id === p.bank_transaction_id);
  const refunds = useApi((signal) => api.listPaymentRequestRefunds(p.id, { signal }), [p.id, detail]);
  const sourceRefunds = refunds.status === 'success' ? refunds.data.items : [];
  const actions = (
    <>
      {open && mayHandlePayments(access.grants, access.client) ? (
        <ActionDialog label={t('payment.confirm.action')} icon={CircleCheck} title={t('payment.confirm.title')} description={t('payment.confirm.description')} onChanged={reload}>
          {(d) => <ConfirmForm detail={detail} {...d} />}
        </ActionDialog>
      ) : null}
      {open && mayMakeCodes(access.grants, access.client) ? (
        <ActionDialog label={t('payment.cancel.action')} icon={CircleX} title={t('payment.cancel.title')} description={t('payment.cancel.description')} onChanged={reload}>
          {(d) => <CancelForm id={p.id} {...d} />}
        </ActionDialog>
      ) : null}
      <RefreshButton onClick={reload} />
    </>
  );
  return (
    <>
      <PageHeader title={p.code ? t('payment.titleFor', { code: p.code }) : t('payment.title')} crumbs={[...crumbs, { label: p.customer_name }]} actions={actions} />
      <div className="flex flex-col gap-6">
        <StatGrid className="xl:grid-cols-3">
          <StatCard label={t('common.status')} value={label('paymentStatus', p.status)} tone={PAYMENT_STATUS[p.status] ?? 'neutral'} hint={label('paymentPurpose', p.purpose)} />
          <StatCard label={t('common.amount')} value={<Amount amount={p.amount} currency={p.currency} />} />
          <StatCard label={t('payment.field.settled')} value={<Amount amount={p.settled_amount} currency={p.currency} />} />
        </StatGrid>
        <Section>
          <KeyValue
            columns={3}
            rows={[
              [t('payment.field.code'), p.code ? <CopyText key="c" value={p.code} /> : null],
              open && detail.pay_link ? [t('payment.field.payLink'), <CopyText key="pl" value={detail.pay_link} />] : null,
              [t('common.customer'), <CustomerRef key="cu" id={p.customer_id} name={p.customer_name} />],
              [t('common.purpose'), <Label key="pu" group="paymentPurpose" value={p.purpose} />],
              [t('common.order'), p.order_id ? <OrderRef key="o" id={p.order_id} domain={p.order_domains?.split(', ')[0] ?? null} /> : null],
              [t('payment.field.orderStatus'), p.order_status ? <Status key="os" group="orderStatus" tones={ORDER_STATUS} value={p.order_status} /> : null],
              [
                t('payment.field.method'),
                p.receiving_account
                  ? [p.receiving_account.bank_name, p.receiving_account.account_number, p.receiving_account.account_name].filter(Boolean).join(' · ')
                  : null,
              ],
              [t('payment.field.provider'), `${p.method} · ${p.instance_key ?? 'default'}`],
              [t('payment.field.expires'), p.expires_at ? formatDateTime(p.expires_at) : null],
              [t('payment.field.settlement'), p.settlement_entry_id ? <JournalRef key="e" id={p.settlement_entry_id} /> : null],
              [
                t('payment.field.bankTransaction'),
                settledTx ? (
                  <Link key="bt" to={bankTxPath(settledTx.id)} className="text-primary hover:underline">
                    {settledTx.bank_reference ?? settledTx.external_id}
                  </Link>
                ) : null,
              ],
              [t('common.created'), formatDateTime(p.created_at)],
              [t('payment.field.id'), <CopyText key="id" value={p.id} />],
            ]}
          />
        </Section>
        <Section title={t('payment.qr.title')} description={open && p.code ? t('payment.qr.hint', { code: p.code }) : undefined}>
          <div className="space-y-3">
            <QrCard detail={detail} link={false} />
            {detail.qr ? (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">{t('payment.qr.payload')}</summary>
                <code className="mt-2 block break-all font-mono">{detail.qr.payload}</code>
              </details>
            ) : null}
          </div>
        </Section>
        <Tabs defaultValue="transactions">
          <TabsList className="max-w-full justify-start overflow-x-auto">
            <TabsTrigger value="transactions">{t('payment.tab.transactions', { count: detail.transactions.length })}</TabsTrigger>
            <TabsTrigger value="sessions">{t('payment.tab.sessions', { count: detail.sessions.length })}</TabsTrigger>
            <TabsTrigger value="approvals">{t('payment.tab.approvals', { count: detail.approvals.length })}</TabsTrigger>
            <TabsTrigger value="refunds">{t('payment.tab.sourceRefunds', { count: sourceRefunds.length })}</TabsTrigger>
          </TabsList>
          <TabsContent value="transactions">
            <Section flush>
              <BankTxTable rows={detail.transactions} empty={t('payment.noTransactions')} showRequest={false} />
            </Section>
          </TabsContent>
          <TabsContent value="sessions">
            <Section flush>
              <p className="px-5 pb-3 text-sm text-muted-foreground">{t('payment.sessionsHint')}</p>
              <Sessions rows={detail.sessions} refunds={sourceRefunds} threshold={detail.confirm_threshold} reload={reload} />
            </Section>
          </TabsContent>
          <TabsContent value="approvals">
            <Section flush>
              <Approvals rows={detail.approvals} />
            </Section>
          </TabsContent>
          <TabsContent value="refunds">
            <Section flush>
              <p className="px-5 pb-3 text-sm text-muted-foreground">{t('payment.refundsHint')}</p>
              {refunds.status === 'error' ? (
                <div className="px-5 pb-5">
                  <DataError error={refunds.error} onRetry={refunds.reload} title={t('payment.loadError')} />
                </div>
              ) : (
                <SourceRefunds rows={sourceRefunds} threshold={detail.confirm_threshold} reload={refunds.reload} />
              )}
            </Section>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

/** A payment that is not a QR request (an earlier kind): its record and refunds only. */
function OtherPayment({ payment: p, refunds, reload }: { payment: Payment; refunds: Refund[]; reload: () => void }) {
  const { t } = useTranslation('commerce');
  const crumbs = [{ label: t('crumb') }, { label: t('payments.title'), to: '/sales?tab=payments' }];
  return (
    <>
      <PageHeader title={t('payment.title')} crumbs={[...crumbs, { label: p.customer_name }]} actions={<RefreshButton onClick={reload} />} />
      <div className="flex flex-col gap-6">
        <Callout tone="neutral">{t('payment.notRequest')}</Callout>
        <Section>
          <KeyValue
            columns={3}
            rows={[
              [t('common.status'), <Status key="s" group="paymentStatus" tones={PAYMENT_STATUS} value={p.status} />],
              [t('common.amount'), <Amount key="a" amount={p.amount} currency={p.currency} className="font-semibold" />],
              [t('common.purpose'), <Label key="pu" group="paymentPurpose" value={p.purpose} />],
              [t('common.customer'), <CustomerRef key="c" id={p.customer_id} name={p.customer_name} />],
              [t('payment.field.provider'), p.provider],
              [t('payment.field.reference'), p.provider_reference ? <CopyText key="r" value={p.provider_reference} /> : null],
              [t('common.order'), <OrderRef key="o" id={p.order_id} domain={p.order_domain} count={p.order_item_count} at={p.order_placed_at} />],
              [t('payment.field.settlement'), <JournalRef key="e" id={p.settlement_entry_id} description={p.settlement_description} />],
              [t('common.created'), formatDateTime(p.created_at)],
              [t('payment.field.id'), <CopyText key="id" value={p.id} />],
            ]}
          />
        </Section>
        <Section flush title={t('payment.tab.refunds', { count: refunds.length })}>
          <RefundsTable rows={refunds} empty={t('payment.noRefunds')} showCustomer={false} />
        </Section>
      </div>
    </>
  );
}

/** `/payments/:paymentId`: a payment request (or, for an earlier kind of payment, its record). */
export function PaymentDetailPage() {
  const { t } = useTranslation('commerce');
  const { paymentId = '' } = useParams();
  useDocumentTitle(t('payment.title'));
  const result = useApi(
    async (signal) => {
      try {
        return { kind: 'request' as const, detail: await api.getPaymentRequest(paymentId, { signal }) };
      } catch (e) {
        if (isApiError(e) && e.status === 404) return { kind: 'other' as const, detail: await api.getPayment(paymentId, { signal }) };
        throw e;
      }
    },
    [paymentId],
  );
  const crumbs = [{ label: t('crumb') }, { label: t('payments.title'), to: '/sales?tab=payments' }];
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') {
    return (
      <>
        <PageHeader title={t('payment.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={result.reload} title={t('payment.loadError')} />
      </>
    );
  }
  const r = result.data;
  return r.kind === 'request' ? (
    <RequestDetail detail={r.detail} reload={result.reload} />
  ) : (
    <OtherPayment payment={r.detail.payment} refunds={r.detail.refunds} reload={result.reload} />
  );
}

/** `/refunds/:refundId`: one refund, the entry that moved the money back and the credit note. */
export function RefundDetailPage() {
  const { t } = useTranslation('commerce');
  const label = useEnumLabel('commerce');
  const { refundId = '' } = useParams();
  useDocumentTitle(t('refund.title'));
  const result = useApi((signal) => api.getRefund(refundId, { signal }), [refundId]);
  const crumbs = [{ label: t('crumb') }, { label: t('payments.title'), to: '/payments#refunds' }];
  if (result.status === 'loading') return <Loading rows={4} />;
  if (result.status === 'error') {
    return (
      <>
        <PageHeader title={t('refund.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={result.reload} title={t('refund.loadError')} />
      </>
    );
  }
  const { refund: r, entry, payment, credit_notes } = result.data;
  return (
    <>
      <PageHeader
        title={r.domain_name ? t('refund.titleFor', { domain: r.domain_name }) : t('refund.title')}
        crumbs={[...crumbs, { label: r.domain_name ?? t('refund.title') }]}
        actions={<RefreshButton onClick={result.reload} />}
      />
      <div className="flex flex-col gap-6">
        <StatGrid className="xl:grid-cols-2">
          <StatCard label={t('refund.stat.status')} value={label('refundStatus', r.status)} tone={REFUND_STATUS[r.status] ?? 'neutral'} />
          <StatCard label={t('refund.stat.amount')} value={<Amount amount={r.amount} currency={r.currency} />} hint={payment ? undefined : t('refund.fromWallet')} />
        </StatGrid>
        <Section>
          <KeyValue
            columns={3}
            rows={[
              [t('common.status'), <Status key="s" group="refundStatus" tones={REFUND_STATUS} value={r.status} />],
              [t('common.amount'), <Amount key="a" amount={r.amount} currency={r.currency} className="font-semibold" />],
              [t('common.domain'), r.domain_name ? <DomainLink key="d" keyOrName={r.domain_name} /> : null],
              [t('common.customer'), <CustomerRef key="c" id={r.customer_id} name={r.customer_name} />],
              [t('common.order'), <OrderRef key="o" id={r.order_id} domain={r.order_domain} count={r.order_item_count} at={r.order_placed_at} />],
              [t('refund.field.itemStatus'), r.item_status ? <Status key="is" group="orderItemStatus" tones={ORDER_ITEM_STATUS} value={r.item_status} /> : null],
              [t('refund.field.reason'), <SystemReason key="r" reason={r.reason} />],
              [t('refund.field.entry'), <JournalRef key="e" id={r.journal_entry_id} description={r.entry_description} />],
              [t('refund.field.payment'), payment ? <PaymentRef key="p" payment={payment} /> : <span key="p" className="text-muted-foreground">{t('refund.fromWallet')}</span>],
              [t('refund.field.created'), formatDateTime(r.created_at)],
              [t('refund.field.id'), <CopyText key="id" value={r.id} />],
            ]}
          />
        </Section>
        <Section title={t('refund.entry')} flush>
          {entry ? <PostingsTable postings={entry.postings} /> : <p className="px-5 pb-5 text-sm text-muted-foreground">{t('refund.noEntry')}</p>}
        </Section>
        <Section title={t('refund.creditNotes')}>
          {credit_notes.length ? (
            <ul className="space-y-1.5 text-sm">
              {credit_notes.map((cn) => (
                <li key={cn.id} className="flex flex-wrap items-center gap-2">
                  <Status group="docType" tones={DOC_TYPE} value={cn.document_type} />
                  <InvoiceRef invoice={cn} />
                  <Amount amount={cn.total} currency={cn.currency} />
                  <span className={mutedSmall}>{t('order.issuedAt', { at: formatDateTime(cn.issued_at) })}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">{t('refund.noCreditNotes')}</p>
          )}
        </Section>
      </div>
    </>
  );
}
